/**
 * P3 — persistent state-indicator overlays that hang off a single
 * mech `<group>`. Each component is a "render this when the
 * corresponding MechState flag is set" widget; they're idempotent
 * (no event timers, no rising edges) so the consumer just mounts
 * them unconditionally and they self-suppress when the flag is off.
 *
 *   <TaggedMarker tagged accent="#fa6" />
 *   <EvasionMotes pips={mech.evasion_pips} accent="..." />
 *   <SmokeWisps active={mech.smoke_active} />
 *   <ScanLine active={mech.scanned_this_turn} accent="..." />
 *
 * Local-coordinate units assume the mech group is centered on the
 * chassis hex at world Y≈0.5 (`hexToWorld(_, 0).y === 0`; ArchetypeMech
 * offsets to Y=0.5 in target). Voxel scale 0.025 world units / voxel.
 */

import { useMemo, useRef } from "react";
import {
  type Color as ColorT,
  Color,
  type InstancedMesh,
  Matrix4,
  type Mesh,
} from "three";
import { useFrame } from "@react-three/fiber";

// Heights chosen relative to the chassis (~1u tall composed envelope).
const HEAD_TOP_LOCAL_Y = 1.05;
const CHASSIS_MID_LOCAL_Y = 0.55;

// ── Tagged marker ──────────────────────────────────────────────────────

const TAGGED_BOB_AMPLITUDE = 0.04;
const TAGGED_BOB_FREQ_HZ = 0.9;

export function TaggedMarker({ tagged, accent }: { tagged: boolean; accent: string }) {
  const ref = useRef<Mesh>(null);
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    m.visible = tagged;
    if (!tagged) return;
    const now = performance.now();
    const phase = (now / 1000) * TAGGED_BOB_FREQ_HZ * Math.PI * 2;
    m.position.y = HEAD_TOP_LOCAL_Y + Math.sin(phase) * TAGGED_BOB_AMPLITUDE;
    m.rotation.y = phase * 0.5;
  });
  return (
    <mesh ref={ref} visible={false} position={[0, HEAD_TOP_LOCAL_Y, 0]}>
      <octahedronGeometry args={[0.05]} />
      <meshStandardMaterial
        color={accent}
        emissive={accent}
        emissiveIntensity={1.6}
      />
    </mesh>
  );
}

// ── Evasion motes ──────────────────────────────────────────────────────

const EVASION_ORBIT_RADIUS = 0.34;
const EVASION_ORBIT_HEIGHT = CHASSIS_MID_LOCAL_Y;
const EVASION_ORBIT_FREQ_HZ = 0.45;
const EVASION_MAX_PIPS = 3;
const _evasionScratch = new Matrix4();

export function EvasionMotes({
  pips,
  accent,
}: {
  pips: number;
  accent: string;
}) {
  const meshRef = useRef<InstancedMesh>(null);
  // Stable color object so the material doesn't allocate per frame.
  const color = useMemo(() => new Color(accent), [accent]);
  useFrame(() => {
    const m = meshRef.current;
    if (!m) return;
    const active = Math.max(0, Math.min(EVASION_MAX_PIPS, Math.floor(pips)));
    m.count = active;
    m.visible = active > 0;
    if (active === 0) return;
    const now = performance.now();
    const baseAngle = (now / 1000) * EVASION_ORBIT_FREQ_HZ * Math.PI * 2;
    for (let i = 0; i < active; i++) {
      // Evenly distribute around the chassis on the same orbit ring.
      // Divide by `active` (not MAX) so partial counts read centered:
      // 2 pips at 0°/180°, 3 pips at 0°/120°/240°.
      const phase = baseAngle + (i / active) * Math.PI * 2;
      const x = Math.cos(phase) * EVASION_ORBIT_RADIUS;
      const z = Math.sin(phase) * EVASION_ORBIT_RADIUS;
      _evasionScratch.makeTranslation(x, EVASION_ORBIT_HEIGHT, z);
      m.setMatrixAt(i, _evasionScratch);
    }
    m.instanceMatrix.needsUpdate = true;
  });
  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, EVASION_MAX_PIPS]}
      visible={false}
    >
      <icosahedronGeometry args={[0.035]} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.4} />
    </instancedMesh>
  );
}

// ── Smoke wisps ────────────────────────────────────────────────────────
//
// Cheap particle pool: N small dark voxels rise from the chassis center,
// each on its own cosine-eased life cycle. Pure imperative writes to the
// InstancedMesh — no per-frame React renders.

const SMOKE_COUNT = 8;
const SMOKE_LIFE_MS = 1600;
const SMOKE_RISE_HEIGHT = 0.55;
const SMOKE_RADIUS = 0.025;
const _smokeScratch = new Matrix4();
const _smokeColor = new Color("#2a2f3a");

interface SmokeParticle {
  startMs: number;
  jitterX: number;
  jitterZ: number;
}

export function SmokeWisps({ active }: { active: boolean }) {
  const meshRef = useRef<InstancedMesh>(null);
  const particles = useRef<SmokeParticle[]>(
    Array.from({ length: SMOKE_COUNT }, () => ({
      startMs: -Infinity,
      jitterX: 0,
      jitterZ: 0,
    })),
  );

  useFrame(() => {
    const m = meshRef.current;
    if (!m) return;
    const now = performance.now();
    m.visible = active;
    if (!active) {
      // Hide all instances by zero-scaling — cheaper than re-mounting.
      for (let i = 0; i < SMOKE_COUNT; i++) {
        _smokeScratch.makeScale(0, 0, 0);
        m.setMatrixAt(i, _smokeScratch);
      }
      m.instanceMatrix.needsUpdate = true;
      return;
    }
    // Spawn / respawn pass.
    for (let i = 0; i < SMOKE_COUNT; i++) {
      const p = particles.current[i];
      const elapsed = now - p.startMs;
      if (elapsed > SMOKE_LIFE_MS || p.startMs < 0) {
        // Stagger spawns so the pool doesn't all reset together.
        p.startMs = now - (i / SMOKE_COUNT) * SMOKE_LIFE_MS;
        p.jitterX = (Math.random() - 0.5) * 0.12;
        p.jitterZ = (Math.random() - 0.5) * 0.12;
      }
    }
    // Write matrices.
    for (let i = 0; i < SMOKE_COUNT; i++) {
      const p = particles.current[i];
      const t = Math.min(1, Math.max(0, (now - p.startMs) / SMOKE_LIFE_MS));
      const y = CHASSIS_MID_LOCAL_Y + t * SMOKE_RISE_HEIGHT;
      // Scale shrinks at end-of-life; cosine fade in over first 12%.
      const grow = Math.min(1, t / 0.12);
      const fade = 1 - t;
      const s = SMOKE_RADIUS * grow * fade * 8; // 8× voxel scale for readability
      _smokeScratch.makeScale(s, s, s);
      _smokeScratch.setPosition(p.jitterX, y, p.jitterZ);
      m.setMatrixAt(i, _smokeScratch);
    }
    m.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, SMOKE_COUNT]}
      visible={false}
    >
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial
        color={_smokeColor}
        transparent
        opacity={0.55}
      />
    </instancedMesh>
  );
}

// ── Crit-part smoke (B7) ──────────────────────────────────────────────
//
// Per-part smoke emitter — same particle pool shape as SmokeWisps but
// anchored at an arbitrary local position so we can fire multiple
// emitters per chassis (one per damaged part). Severity drives both
// particle count and emission rate.

const CRIT_SMOKE_MAX = 6;
const CRIT_SMOKE_LIFE_MS = 1400;
const CRIT_SMOKE_RISE = 0.4;
const _critScratch = new Matrix4();
const _critColor = new Color("#3a3030");

interface PartSmokeProps {
  /** 0..n severity. 0 hides. Capped at 3 for visible scaling. */
  severity: number;
  /** Local position (relative to mech `<group>`). */
  origin: [number, number, number];
  /** Optional tint override (defaults to dark smoke). */
  color?: string;
}

export function PartSmoke({ severity, origin, color }: PartSmokeProps) {
  const meshRef = useRef<InstancedMesh>(null);
  const particles = useRef<SmokeParticle[]>(
    Array.from({ length: CRIT_SMOKE_MAX }, () => ({
      startMs: -Infinity,
      jitterX: 0,
      jitterZ: 0,
    })),
  );
  const tinted = useMemo(() => (color ? new Color(color) : _critColor), [color]);
  useFrame(() => {
    const m = meshRef.current;
    if (!m) return;
    const active = severity > 0;
    m.visible = active;
    if (!active) {
      for (let i = 0; i < CRIT_SMOKE_MAX; i++) {
        _critScratch.makeScale(0, 0, 0);
        m.setMatrixAt(i, _critScratch);
      }
      m.instanceMatrix.needsUpdate = true;
      return;
    }
    // Severity scales particle count linearly to 3.
    const visibleCount = Math.min(
      CRIT_SMOKE_MAX,
      Math.ceil(Math.min(3, severity) * (CRIT_SMOKE_MAX / 3)),
    );
    const now = performance.now();
    for (let i = 0; i < CRIT_SMOKE_MAX; i++) {
      if (i >= visibleCount) {
        _critScratch.makeScale(0, 0, 0);
        m.setMatrixAt(i, _critScratch);
        continue;
      }
      const p = particles.current[i];
      const elapsed = now - p.startMs;
      if (elapsed > CRIT_SMOKE_LIFE_MS || p.startMs < 0) {
        p.startMs = now - (i / visibleCount) * CRIT_SMOKE_LIFE_MS;
        p.jitterX = (Math.random() - 0.5) * 0.08;
        p.jitterZ = (Math.random() - 0.5) * 0.08;
      }
      const t = Math.min(1, (now - p.startMs) / CRIT_SMOKE_LIFE_MS);
      const y = origin[1] + t * CRIT_SMOKE_RISE;
      const grow = Math.min(1, t / 0.1);
      const fade = 1 - t;
      const s = 0.025 * grow * fade * 6;
      _critScratch.makeScale(s, s, s);
      _critScratch.setPosition(origin[0] + p.jitterX, y, origin[2] + p.jitterZ);
      m.setMatrixAt(i, _critScratch);
    }
    m.instanceMatrix.needsUpdate = true;
  });
  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, CRIT_SMOKE_MAX]}
      visible={false}
    >
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial
        color={tinted}
        transparent
        opacity={0.55}
        emissive="#1a1010"
        emissiveIntensity={0.2}
      />
    </instancedMesh>
  );
}

// ── Target-lock reticle (B7) ──────────────────────────────────────────
//
// Floating ring above the head; rotates + pulses scale when the mech
// has at least one target lock on it. Accent-colored to match the
// chassis's palette so each side reads visually distinct.

const RETICLE_LOCAL_Y = 1.18;
const RETICLE_RADIUS = 0.16;
const RETICLE_PULSE_FREQ_HZ = 1.6;

export function TargetLockReticle({
  locks,
  accent,
}: {
  locks: number;
  accent: string;
}) {
  const ref = useRef<Mesh>(null);
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    const active = locks > 0;
    m.visible = active;
    if (!active) return;
    const now = performance.now();
    m.rotation.z = (now / 1000) * 0.7;
    const phase = (now / 1000) * RETICLE_PULSE_FREQ_HZ * Math.PI * 2;
    // Pulse scales between 1.0 and 1.18; multiple locks bump the
    // base size so 2-3 locks read tactically distinct from 1.
    const lockK = 1 + Math.min(3, locks - 1) * 0.12;
    m.scale.setScalar(lockK * (1 + 0.08 * Math.sin(phase)));
  });
  return (
    <mesh ref={ref} visible={false} position={[0, RETICLE_LOCAL_Y, 0]}>
      <torusGeometry args={[RETICLE_RADIUS, 0.012, 6, 24]} />
      <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={1.4} />
    </mesh>
  );
}

// ── Scan-line ──────────────────────────────────────────────────────────
//
// One-shot horizontal slice that travels up the chassis when
// `scanned_this_turn` rises. Held thin so it reads as a sweep, not a
// halo. Auto-clears once the sweep completes; consumer is free to keep
// the flag set across multiple turns and we'll just sweep on each
// rising edge.

const SCAN_DURATION_MS = 600;
const SCAN_BOTTOM_Y = 0.05;
const SCAN_TOP_Y = 1.1;
const SCAN_THICKNESS = 0.04;

export function ScanLine({
  active,
  accent,
}: {
  active: boolean;
  accent: string;
}) {
  const ref = useRef<Mesh>(null);
  const startMsRef = useRef<number | null>(null);
  const prevActiveRef = useRef(false);

  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    const now = performance.now();
    // Rising edge → start a fresh sweep.
    if (active && !prevActiveRef.current) {
      startMsRef.current = now;
    }
    prevActiveRef.current = active;

    if (startMsRef.current === null) {
      m.visible = false;
      return;
    }
    const elapsed = now - startMsRef.current;
    if (elapsed >= SCAN_DURATION_MS) {
      m.visible = false;
      startMsRef.current = null;
      return;
    }
    m.visible = true;
    const t = elapsed / SCAN_DURATION_MS;
    m.position.y = SCAN_BOTTOM_Y + (SCAN_TOP_Y - SCAN_BOTTOM_Y) * t;
    const mat = m.material as { opacity?: number; color?: ColorT };
    if (mat.opacity !== undefined) {
      // Brighten in the first 20%, fade in the last 30%.
      const inK = Math.min(1, t / 0.2);
      const outK = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
      mat.opacity = inK * outK * 0.85;
    }
    if (mat.color) mat.color.set(accent);
  });

  return (
    <mesh ref={ref} visible={false}>
      <boxGeometry args={[0.7, SCAN_THICKNESS, 0.7]} />
      <meshBasicMaterial color={accent} transparent opacity={0} />
    </mesh>
  );
}
